import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Lightbulb, ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const VISION_POINTS = [
  "Stessi criteri e stesso framework di competenze dalla selezione allo sviluppo: chi entra viene già valutato con la logica con cui verrà accompagnato domani",
  "Assessment via WhatsApp: nella GDO non è una scelta di comodo, è una scelta strategica. Abbatte ogni barriera di accesso e porta i tassi di completamento a livelli che altri strumenti non raggiungono",
  "Estensione della valutazione agli Store Manager in carica, per identificare i profili pronti al ruolo di Area Manager",
  "Selezione, sviluppo e formazione integrati in un sistema continuo. Non più processi separati, ma un'unica talent strategy che funziona come asset di business",
];

export default function VisionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section
      id="visione"
      data-testid="vision-section"
      className="py-20 md:py-28 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Evolution 2026 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16"
        >
          <Badge
            data-testid="vision-badge"
            className="mb-4 bg-indigo-50 text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-medium text-xs tracking-wide uppercase px-3 py-1"
          >
            Evoluzione 2026
          </Badge>
          <h2
            data-testid="vision-title"
            className="font-['Inter_Tight'] text-3xl md:text-4xl font-bold tracking-tight text-[var(--sv-secondary)] mb-3"
          >
            Da sviluppo interno a talent strategy integrata
          </h2>
          <p className="text-base text-slate-500 max-w-2xl mb-8">
            Quando l'assessment sullo sviluppo interno ha iniziato a produrre risultati concreti e scalabili, la scelta naturale è stata estenderlo anche alla selezione esterna, eliminando la discontinuità tra "chi cerchiamo" e "come lo facciamo crescere".
          </p>

          <Card
            data-testid="vision-card"
            className="p-6 md:p-8 border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-white"
          >
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-['Inter_Tight'] font-bold text-lg text-[var(--sv-secondary)]">
                  Obiettivo strategico
                </h3>
                <p className="text-sm text-slate-500 mt-1">
                  Costruire un ciclo unico e coerente in cui ogni persona, dal primo giorno, viene valutata, orientata e accompagnata con la stessa logica.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              {VISION_POINTS.map((point, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -12 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.2 + i * 0.1 }}
                  data-testid={`vision-point-${i}`}
                  className="flex items-start gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ArrowRight className="w-3 h-3 text-indigo-600" />
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {point}
                  </p>
                </motion.div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}
